import { api } from '../../services/api';
import { mapServerResearchToLocal, mapServerResponsesToLocal } from '../../services/apiUtils';
import { paymentApi } from '../../services/paymentApi';

export async function payCredit(updateUserAttributes, setError, navigate, setLoading) {
  const creditResponse = await paymentApi.useCredit();

  if (!creditResponse.success) {
    setError(creditResponse.message || 'Insufficient credits to generate responses.');

    if (creditResponse.redirectToTopUp) {
      navigate('/account', {
        state: {
          message: 'Please sign up to our beta to get unrestricted access to our platform'
        }
      });
    }

    setLoading(false);
    return false;
  }

  // Update the user's credit count in the local state
  if (updateUserAttributes) {
    await updateUserAttributes();
  }

  return true;
}

export async function refundCredit(updateUserAttributes) {
  try {
    const refundResponse = await paymentApi.refundCredit();

    if (refundResponse.success) {
      console.log("Credit refunded successfully");

      // Update the user's credit count in the local state
      if (updateUserAttributes) {
        await updateUserAttributes();
      }

      return true;

    } else {
      console.error("Failed to refund credit:", refundResponse);
      return false;
    }
  } catch (refundError) {
    console.error("Error refunding credit:", refundError);
    return false;
  }
}

export async function pollResponses(userID, researchID, numberOfPersonas, onProgress, onPartialResults) {
  let stopPolling = false;
  let lastResponseCount = 0;

  // Start both polls in parallel
  const researchPromise = pollResearchStatus(userID, researchID, () => stopPolling);

  // Modified polling function that reports partial results
  const responsesPromise = new Promise(async (resolve, reject) => {
    try {
      let attempts = 0;
      const maxAttempts = 300; // Greatly increased (25 minutes at 5s interval)

      while (attempts < maxAttempts) {
        const responses = await api.getExistingResponses(userID, researchID);

        // Calculate progress
        const responseCount = responses?.responses?.length || 0;
        const progress = numberOfPersonas > 0 ? Math.min((responseCount / numberOfPersonas) * 100, 100) : 0;
        onProgress(progress);

        // If we have new responses, notify the component
        if (responseCount > lastResponseCount && typeof onPartialResults === 'function') {
          lastResponseCount = responseCount;
          // Process and send the partial results
          const localResponses = mapServerResponsesToLocal(responses.responses, researchID);
          onPartialResults(localResponses);
        }

        // Check if complete or if polling should stop
        if (responseCount >= numberOfPersonas || stopPolling) {
          resolve(responses);
          return;
        }

        await new Promise(resolve => setTimeout(resolve, 5000));
        attempts++;
      }

      // If we get here, we timed out but might have partial results
      if (lastResponseCount > 0) {
        const responses = await api.getExistingResponses(userID, researchID);
        resolve(responses); // Return partial results instead of failing
      } else {
        reject(new Error('Timeout waiting for any responses'));
      }
    } catch (error) {
      reject(error);
    }
  });

  // When responses are done, stop polling research
  responsesPromise.finally(() => {
    stopPolling = true;
  });

  try {
    // Wait for both to finish
    const [research, responses] = await Promise.all([researchPromise, responsesPromise]);

    const localResearch = mapServerResearchToLocal(research);
    const localResponses = mapServerResponsesToLocal(responses.responses, researchID);
    return { ...localResearch, surveyResponses: localResponses };
  } catch (error) {
    // Check if we have any partial responses before throwing
    const responses = await api.getExistingResponses(userID, researchID);
    if (responses?.responses?.length > 0) {
      // We have partial results, return them instead of failing
      const localResponses = mapServerResponsesToLocal(responses.responses, researchID);
      return {
        responsesStatus: 'PARTIAL', // New status to indicate partial completion
        surveyResponses: localResponses
      };
    }
    throw error; // Re-throw if no responses at all
  }
}

export async function pollResearchStatus(userID, researchID, getStopPolling, maxAttempts = 300, pollInterval = 10000) {
  let attempts = 0;
  let errorCount = 0;
  const maxErrorCount = 3;

  while (attempts < maxAttempts) {
    try {
      const research = await api.getPreviousResearch(userID, researchID);
      const stopPolling = getStopPolling();
      console.log("INSIDE POLLRESEARCHSTATUS, stopPolling", stopPolling);
      if (stopPolling) return research;

      if (research?.responsesStatus === 'COMPLETED') {
        return research;
      } else if (research?.responsesStatus === 'ERROR') {
        errorCount++;
        if (errorCount >= maxErrorCount) {
          throw new Error('Failed to generate responses after multiple ERROR statuses');
        }
      } else {
        errorCount = 0;
      }

      await new Promise(resolve => setTimeout(resolve, pollInterval));
      attempts++;
    } catch (error) {
      await new Promise(resolve => setTimeout(resolve, pollInterval));
      attempts++;
    }
  }
  throw new Error('Timeout waiting for generation to complete');
}

export async function pollResponsesIter(
  userID,
  researchID,
  targetIteration,
  onProgress, maxAttempts = 300,
  pollInterval = 5000
) {
  let attempts = 0;
  // normalize and guard
  const target = parseInt(targetIteration || 0, 10);

  while (attempts < maxAttempts) {
    const responses = await api.getExistingResponses(userID, researchID);

    // normalize and guard
    const iter = parseInt(responses?.iter || 0, 10);

    const progress = target > 0 ? Math.min((iter / target) * 100, 100) : 0;
    onProgress(progress);

    console.log("INSIDE POLLRESPONSESITER, responses.iter is", iter, "and targetIteration is", target);
    console.log("responses.iter === targetIteration is", iter === target);
    console.log(`[pollResponsesIter] iter=${iter} target=${target} attempt=${attempts + 1}`);

    // Use >= to avoid strict-equality race conditions
    if (iter >= target) {
      return responses;
    }

    await new Promise(resolve => setTimeout(resolve, pollInterval));
    attempts++;
  }
  throw new Error('Timeout waiting for generation to complete');
}

